import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus } from "@/api/dtos";
import ConfigurationTrialContext from "@/contexts/data/ConfigurationTrialContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import TrialContext from "@/contexts/data/TrialContext";
import useConfigurationCoordinatingGroup from "@/hooks/data/useConfigurationCoordinatingGroup";
import useConfigurationInstitution from "@/hooks/data/useConfigurationInstitution";
import useConfigurationMasterGroup from "@/hooks/data/useConfigurationMasterGroup";
import useConfigurationParticipant from "@/hooks/data/useConfigurationParticipant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Button, IconButton, alertClasses, darken, lighten, styled } from "@mui/material";
import { grey } from "@mui/material/colors";
import MuiMarkdown from "mui-markdown";
import { FC, useContext, useEffect, useState } from "react";

const StyledAlert = styled(Alert)(({ theme }) => ({ 
    overflow: 'auto',
    marginBottom: theme.spacing(2)
}));

export const DefaultAlert = styled(Alert)(({ theme }) => {
 
    const getColor = theme.palette.mode === 'light' ? darken : lighten;
    const getBackgroundColor = theme.palette.mode === 'light' ? lighten : darken;
    const color = grey[900];


    return { 
        overflow: 'auto',
        marginBottom: theme.spacing(2),
        color: getColor(color, 0.6),
        backgroundColor: getBackgroundColor(color, 0.9),

        [`& .${alertClasses.icon}`]: {
            color: getColor(color, 0.6)
        }
    }
});

interface IMessagesViewProps {
    entityName: string;
    storageKey: string;
    alertSuccess?: string;
    alertInformation?: string;
    alertWarning?: string;
    alertError?: string;
    alertDefault?: string;
}

const useSessionStorage = <T,>(
    key: string,
    initialValue?: T,
    raw?: boolean
  ): [T, (value: T) => void] => {
  
    const getData = () => {
        try {
            const sessionStorageValue = sessionStorage.getItem(key);
            if (typeof sessionStorageValue !== 'string') {
            sessionStorage.setItem(key, raw ? String(initialValue) : JSON.stringify(initialValue));
            return initialValue;
            } else {
            return raw ? sessionStorageValue : JSON.parse(sessionStorageValue || 'null');
            }
        } catch {
            // If user is in private mode or has storage restriction
            // sessionStorage can throw. JSON.parse and JSON.stringify
            // can throw, too.
            return initialValue;
        }
    };
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [state, setState] = useState<T>(getData);
  
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      try {
        const serializedState = raw ? String(state) : JSON.stringify(state);
        sessionStorage.setItem(key, serializedState);
      } catch {
        // If user is in private mode or has storage restriction
        // sessionStorage can throw. Also JSON.stringify can throw.
      }
    }, [state]);

    useEffect(() => {
        setState(getData())
    }, [key])
  
    return [state, setState];
};

export const MessagesView: FC<IMessagesViewProps> = ({
    entityName,
    storageKey,
    alertSuccess,
    alertInformation,
    alertWarning,
    alertError,
    alertDefault
}) => {
    const [hide, setHide] = useSessionStorage(storageKey, {
        success: false,
        information: false,
        warning: false,
        error: false,
        default: false
    }, false)    

    return (
        <>
            {
                alertSuccess && !hide.success && (
                    <StyledAlert
                        severity="success"
                        icon={<FontAwesomeIcon fixedWidth icon={AppSettings.icons.alertSuccess} />}
                        action={
                            <IconButton 
                                color="inherit" 
                                size="small" 
                                onClick={() => {
                                    setHide({
                                        ...hide,
                                        success: true
                                    })
                                }}
                            >
                                <FontAwesomeIcon fixedWidth icon={AppSettings.icons.close} />
                            </IconButton> 
                        }
                    >
                        <AlertTitle>{entityName}</AlertTitle>
                        <MuiMarkdown>{alertSuccess}</MuiMarkdown>
                    </StyledAlert>
                )
            }
            {
                alertInformation && !hide.information && (
                    <StyledAlert
                        severity="info"
                        icon={<FontAwesomeIcon fixedWidth icon={AppSettings.icons.alertInformation} />}
                        action={
                            <IconButton 
                                color="inherit" 
                                size="small" 
                                onClick={() => {
                                    setHide({
                                        ...hide,
                                        information: true
                                    })
                                }}
                            >
                                <FontAwesomeIcon fixedWidth icon={AppSettings.icons.close} />
                            </IconButton> 
                        }
                    >
                        <AlertTitle>{entityName}</AlertTitle>
                        <MuiMarkdown>{alertInformation}</MuiMarkdown>
                    </StyledAlert>
                )
            }
            {
                alertWarning && !hide.warning && (
                    <StyledAlert
                        severity="warning"
                        icon={<FontAwesomeIcon fixedWidth icon={AppSettings.icons.alertWarning} />}
                        action={
                            <IconButton 
                                color="inherit" 
                                size="small" 
                                onClick={() => {
                                    setHide({
                                        ...hide,
                                        warning: true
                                    })
                                }}
                            >
                                <FontAwesomeIcon fixedWidth icon={AppSettings.icons.close} />
                            </IconButton> 
                        }
                    >
                        <AlertTitle>{entityName}</AlertTitle>
                        <MuiMarkdown>{alertWarning}</MuiMarkdown>
                    </StyledAlert>
                )
            }
            {
                alertError && !hide.error && (
                    <StyledAlert
                        severity="error"
                        icon={<FontAwesomeIcon fixedWidth icon={AppSettings.icons.alertError} />}
                        action={
                            <IconButton 
                                color="inherit" 
                                size="small" 
                                onClick={() => {
                                    setHide({
                                        ...hide,
                                        error: true
                                    })
                                }}
                            >
                                <FontAwesomeIcon fixedWidth icon={AppSettings.icons.close} />
                            </IconButton> 
                        }
                    >
                        <AlertTitle>{entityName}</AlertTitle>
                        <MuiMarkdown>{alertError}</MuiMarkdown>
                    </StyledAlert>
                )
            }
            {
                alertDefault && !hide.default && (
                    <DefaultAlert
                        icon={<FontAwesomeIcon fixedWidth icon={AppSettings.icons.alertDefault} />}
                        action={
                            <IconButton 
                                color="inherit" 
                                size="small" 
                                onClick={() => {
                                    setHide({
                                        ...hide,
                                        default: true
                                    })
                                }}
                            >
                                <FontAwesomeIcon fixedWidth icon={AppSettings.icons.close} />
                            </IconButton> 
                        }
                    >
                        <AlertTitle>{entityName}</AlertTitle>
                        <MuiMarkdown>{alertDefault}</MuiMarkdown>
                    </DefaultAlert>
                )
            }
        </>
    )
}


const TrialMessages: FC = () => {
    const trialContext = useContext(TrialContext)
    const context = useContext(ConfigurationTrialContext)

    if (!context.data) {
        return null;
    }

    const alertDefault = context.data ?
        !context.data.status || context.data.status === ConfigurationStatus.Disabled ? 
            `${trialContext.data?.name ? `The ${trialContext.data?.name} trial` : 'This trial'} has been **Disabled** in the Source Data Verification system.` :
            context.data.status === ConfigurationStatus.ReadOnly ?
                `${trialContext.data?.name ? `The ${trialContext.data?.name} trial` : 'This trial'} is set to **Read Only** in the Source Data Verification system.` :
                undefined :
        undefined;

    return (
        <MessagesView
            entityName="Trial"
            storageKey={`Alert-Trial-${context.data?.id}-${context.data?.modifiedDate}`}
            alertSuccess={context.data?.alertSuccess}
            alertInformation={context.data?.alertInformation}
            alertWarning={context.data?.alertWarning}
            alertError={context.data?.alertError}
            alertDefault={alertDefault}
        />
    )
}

const MasterGroupMessages: FC = () => {
    const masterGroupContext = useContext(MasterGroupContext)
    const context = useConfigurationMasterGroup(masterGroupContext.data ? { masterGroupId: masterGroupContext.data?.id } : undefined)

    if (!context.data) {
        return null;
    }

    const alertDefault = context.data ?
        !context.data.status || context.data.status === ConfigurationStatus.Disabled ? 
            `${masterGroupContext.data?.name ? `The ${masterGroupContext.data?.name} master group` : 'This master group'} has been **Disabled** in the Source Data Verification system.` :
            context.data.status === ConfigurationStatus.ReadOnly ?
                `${masterGroupContext.data?.name ? `The ${masterGroupContext.data?.name} master group` : 'This master group'} is set to **Read Only** in the Source Data Verification system.` :
                undefined :
        undefined;

    return (
        <MessagesView
            entityName="MasterGroup"
            storageKey={`Alert-MasterGroup-${context.data?.id}-${context.data?.modifiedDate}`}
            alertSuccess={context.data?.alertSuccess}
            alertInformation={context.data?.alertInformation}
            alertWarning={context.data?.alertWarning}
            alertError={context.data?.alertError}
            alertDefault={alertDefault}
        />
    )
}

const CoordinatingGroupMessages: FC = () => {
    const coordinatingGroupContext = useContext(CoordinatingGroupContext)
    const context = useConfigurationCoordinatingGroup(coordinatingGroupContext.data ? { coordinatingGroupId: coordinatingGroupContext.data?.id } : undefined)

    if (!context.data) {
        return null;
    }

    const alertDefault = context.data ?
        !context.data.status || context.data.status === ConfigurationStatus.Disabled ? 
            `${coordinatingGroupContext.data?.name ? `The ${coordinatingGroupContext.data?.name} coordinating group` : 'This coordinating group'} has been **Disabled** in the Source Data Verification system.` :
            context.data.status === ConfigurationStatus.ReadOnly ?
                `${coordinatingGroupContext.data?.name ? `The ${coordinatingGroupContext.data?.name} coordinating group` : 'This coordinating group'} is set to **Read Only** in the Source Data Verification system.` :
                undefined :
        undefined;

    return (
        <MessagesView
            entityName="CoordinatingGroup"
            storageKey={`Alert-CoordinatingGroup-${context.data?.id}-${context.data?.modifiedDate}`}
            alertSuccess={context.data?.alertSuccess}
            alertInformation={context.data?.alertInformation}
            alertWarning={context.data?.alertWarning}
            alertError={context.data?.alertError}
            alertDefault={alertDefault}
        />
    )
}

const InstitutionMessages: FC = () => {
    const institutionContext = useContext(InstitutionContext)
    const context = useConfigurationInstitution(institutionContext.data ? { institutionId: institutionContext.data?.id } : undefined)

    if (!context.data) {
        return null;
    }

    const alertDefault = context.data ?
        !context.data.status || context.data.status === ConfigurationStatus.Disabled ? 
            `${institutionContext.data?.name ? `The ${institutionContext.data?.name} institution` : 'This institution'} has been **Disabled** in the Source Data Verification system.` :
            context.data.status === ConfigurationStatus.ReadOnly ?
                `${institutionContext.data?.name ? `The ${institutionContext.data?.name} institution` : 'This institution'} is set to **Read Only** in the Source Data Verification system.` :
                undefined :
        undefined;

    return (
        <MessagesView
            entityName="Institution"
            storageKey={`Alert-Institution-${context.data?.id}-${context.data?.modifiedDate}`}
            alertSuccess={context.data?.alertSuccess}
            alertInformation={context.data?.alertInformation}
            alertWarning={context.data?.alertWarning}
            alertError={context.data?.alertError}
            alertDefault={alertDefault}
        />
    )
}

const ParticipantMessages: FC = () => {
    const participantContext = useContext(ParticipantContext)
    const context = useConfigurationParticipant(participantContext.data ? { participantId: participantContext.data?.id } : undefined)

    if (!context.data) {
        return null;
    }

    const alertDefault = context.data ?
        !context.data.status || context.data.status === ConfigurationStatus.Disabled ? 
            `${participantContext.data?.studyNumber ? `The ${participantContext.data?.studyNumber} participant` : 'This participant'} has been **Disabled** in the Source Data Verification system.` :
            context.data.status === ConfigurationStatus.ReadOnly ?
                `${participantContext.data?.studyNumber ? `The ${participantContext.data?.studyNumber} participant` : 'This participant'} is set to **Read Only** in the Source Data Verification system.` :
                undefined :
        undefined;

    return (
        <MessagesView
            entityName="Participant"
            storageKey={`Alert-Participant-${context.data?.id}-${context.data?.modifiedDate}`}
            alertSuccess={context.data?.alertSuccess}
            alertInformation={context.data?.alertInformation}
            alertWarning={context.data?.alertWarning}
            alertError={context.data?.alertError}
            alertDefault={alertDefault}
        />
    )
}

const Messages: FC = () => {
    return (
        <>
            <TrialMessages />
            <MasterGroupMessages />
            <CoordinatingGroupMessages />
            <InstitutionMessages />
            <ParticipantMessages />
        </>
    )
}

export default Messages;